import React from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import type { AxiosError } from "axios";
import { Table } from "../Table/Table";
import type { ITableProps, TableRefProps } from "../Table/utils";
import { TablePageContainer, ToolbarContainer } from "./TablePageContainers";
import { TablePageTitle } from "./TablePageTitle";
import { TablePageActions } from "./TablePageAction";
import { TablePageContent, MainContentWrapper } from "./TablePageContent";
import { TableInlineFilters } from "./TableInlineFilters";
import { Pagination } from "../Pagination/Pagination";
import { DropDown } from "../DropDown/DropDown";
import { ScrollTableWrapper } from "../../pages/SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { TableContainer } from "../../pages/SharedPages/ProductDetailPage/Assets/util";

// ─── Meta row ────────────────────────────────────────────────────────────────

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) =>
    `${theme.primitiveTokens.spacing.spacing_xs} ${theme.primitiveTokens.spacing.spacing_lg}`};
  border-left: ${({ theme }) =>
    `${theme.resolve("border_3xl")} solid ${theme.resolve(
      "color_border_lighter"
    )}`};
  border-right: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.resolve(
      "color_border_lighter"
    )}`};
  border-top: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.resolve(
      "color_border_lighter"
    )}`};
`;

const TotalCountLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
`;

// ─── Types ────────────────────────────────────────────────────────────────────

type PaginationState = {
  pageCount: number;
  pageIndex: number;
  perPage: number;
};

/**
 * All display options for ITableProps that consumers may pass through.
 * We omit the core data props that are declared explicitly.
 */
type TablePassThroughProps<TData extends object> = Omit<
  ITableProps<TData>,
  "columns" | "data" | "isLoading" | "error"
>;

export type TablePageLayoutProps<TData extends object> = {
  // ── Title ──────────────────────────────────────────────────────────────────
  title?: string;
  showTitle?: boolean;
  titleActions?: React.ReactNode;

  // ── Toolbar (search + right-side actions) ──────────────────────────────────
  searchQuery?: string;
  searchPlaceholder?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchClear?: () => void;
  searchRef?: React.Ref<HTMLDivElement>;
  /** Replaces the default search input entirely */
  searchSlot?: React.ReactNode;
  /** Buttons / controls rendered to the right of the search bar */
  toolbarActions?: React.ReactNode;
  /** Set to false to hide the toolbar row (default: true) */
  showToolbar?: boolean;

  // ── Inline filters ─────────────────────────────────────────────────────────
  /** Filter controls (e.g. <FilterBy />, <DateFilter />) */
  filtersSlot?: React.ReactNode;
  showClearAll?: boolean;
  onClearAll?: () => void;
  clearAllLabel?: string;
  /** Set to false to hide the filters row entirely (default: true) */
  showFilters?: boolean;

  // ── Meta row ───────────────────────────────────────────────────────────────
  totalCount?: number;
  /** Noun after the count, e.g. "requests", "products" (default: "results") */
  totalCountLabel?: string;
  searchFilterCount?: number; // e.g. "5 of 20 results" when a search/filter is active
  perPageItems?: number[];
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  /** Set to false to hide the meta row (default: true) */
  showMeta?: boolean;

  // ── Table ──────────────────────────────────────────────────────────────────
  /** When provided, replaces the default <Table> renderer (e.g. TableWithBulkAction, grid view). */
  tableSlot?: React.ReactNode;
  columns?: Array<any>;
  data?: TData[];
  isLoading?: boolean;
  error?: Error | AxiosError | undefined;

  // ── Pagination ─────────────────────────────────────────────────────────────
  pagination?: PaginationState;
  offset?: number;
  onPageChange?: (offset: number) => void;

  // ── Container style override ───────────────────────────────────────────────
  /** Passed directly to the outer TablePageContainer (e.g. padding override) */
  containerStyle?: React.CSSProperties;

  // ── Side panel ─────────────────────────────────────────────────────────────
  /** Rendered alongside the main content (e.g. a filter slide-out panel) */
  sidePanel?: React.ReactNode;

  /** Rendered below the table (e.g. bulk-action toolbars, modals) */
  children?: React.ReactNode;
} & TablePassThroughProps<TData>;

// ─── Component ───────────────────────────────────────────────────────────────

export const TablePageLayout = React.forwardRef(
  <TData extends object>(
    {
      // Title
      title,
      showTitle = false,
      titleActions,

      // Toolbar
      searchQuery,
      searchPlaceholder = "",
      onSearchChange,
      onSearchClear,
      searchRef,
      searchSlot,
      toolbarActions,
      showToolbar = true,

      // Filters
      filtersSlot,
      showClearAll = false,
      onClearAll,
      clearAllLabel,
      showFilters = true,

      // Meta
      totalCount,
      searchFilterCount,
      totalCountLabel,
      perPageItems,
      perPage,
      onPerPageChange,
      showMeta = true,

      // Table
      tableSlot,
      columns,
      data,
      isLoading,
      error,

      // Pagination
      pagination,
      offset = 0,
      onPageChange,

      // Container style
      containerStyle,

      // Side panel / extra children
      sidePanel,
      children,

      // All remaining props are forwarded to <Table />
      ...tableProps
    }: TablePageLayoutProps<TData>,
    ref: React.ForwardedRef<TableRefProps>
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const countLabel = totalCountLabel ?? t("results");
    const showFiltersRow = showFilters && !!filtersSlot;
    const showMetaRow =
      showMeta &&
      (totalCount !== undefined ||
        (perPageItems && perPage !== undefined && onPerPageChange));

    return (
      <TablePageContainer
        style={{
          ...containerStyle,
          marginTop:
            containerStyle?.marginTop ??
            (!showTitle ? theme.resolve("spacing_3xl") : undefined), // Add top margin if title is not shown to compensate for the lack of title row
        }}
      >
        {((showTitle && title) ||
          onSearchChange ||
          searchQuery ||
          searchSlot ||
          toolbarActions) && (
          <ToolbarContainer>
            {showTitle && title && (
              <TablePageTitle
                title={title}
                actions={titleActions}
                showTitle={showTitle}
                showTitleActions={!!titleActions}
              />
            )}
            {(onSearchChange ||
              searchQuery ||
              searchSlot ||
              toolbarActions) && (
              <TablePageActions
                searchQuery={searchQuery}
                searchPlaceholder={searchPlaceholder}
                onSearchChange={onSearchChange}
                onSearchClear={onSearchClear}
                searchRef={searchRef}
                searchSlot={searchSlot}
                actions={toolbarActions}
                showTableActions={showToolbar}
              />
            )}
          </ToolbarContainer>
        )}
        <TablePageContent>
          <MainContentWrapper>
            {showFiltersRow && (
              <TableInlineFilters
                showClearAll={showClearAll}
                onClearAll={onClearAll ?? (() => {})}
                clearAllLabel={clearAllLabel}
              >
                {filtersSlot}
              </TableInlineFilters>
            )}

            {showMetaRow && (
              <MetaRow>
                <TotalCountLabel>
                  {totalCount !== undefined && totalCount > 0
                    ? `${
                        searchFilterCount != null &&
                        searchFilterCount < totalCount
                          ? `${searchFilterCount}/`
                          : ""
                      }${totalCount} ${countLabel}`
                    : null}
                </TotalCountLabel>

                {perPageItems && perPage !== undefined && onPerPageChange && (
                  <DropDown
                    items={perPageItems}
                    activeItem={perPage}
                    textRight={t(" Per Page")}
                    direction="left"
                    className="per_page_dropdown"
                    clickHandler={onPerPageChange}
                  />
                )}
              </MetaRow>
            )}

            {tableSlot ?? (
              <TableContainer>
                <ScrollTableWrapper
                  style={{
                    borderTop: ((): string => {
                      if (!showFiltersRow && !showMetaRow) {
                        return "none"; // Remove top border if filters and meta rows are hidden to avoid double borders with the title row
                      }
                      return `${theme.resolve(
                        "border_md"
                      )} solid ${theme.resolve("color_border_lighter")}`;
                    })(),
                  }}
                  onScroll={tableProps.onScroll}
                  maxHeight={(() => {
                    if (tableProps.maxHeight) return tableProps.maxHeight;
                    const hasPagination = (totalCount ?? 0) > (perPage ?? 20);
                    const hasTitle = showTitle && !!title;
                    const offset =
                      (hasTitle ? 166 : 250) + // Title row height
                      (showToolbar ? 56 : 0) +
                      (showFiltersRow ? 40 : 0) +
                      (showMetaRow ? 40 : 0) +
                      (hasPagination ? 60 : 0);
                    return `calc(100vh - ${offset}px)`;
                  })()}
                  ref={tableProps.scrollRef as React.RefObject<HTMLDivElement>}
                >
                  <Table
                    ref={ref}
                    columns={columns ?? []}
                    data={data ?? []}
                    isLoading={isLoading ?? false}
                    error={error}
                    {...tableProps}
                  />
                </ScrollTableWrapper>
              </TableContainer>
            )}

            {pagination && onPageChange && (
              <Pagination
                pagination={pagination}
                offset={offset}
                handlePageClick={onPageChange}
              />
            )}

            {children}
          </MainContentWrapper>

          {sidePanel}
        </TablePageContent>
      </TablePageContainer>
    );
  }
) as <TData extends object>(
  props: TablePageLayoutProps<TData> & {
    ref?: React.ForwardedRef<TableRefProps>;
  }
) => JSX.Element;
